import { vec3 } from 'gl-matrix';
import { AudioBusId } from './AudioBus';
import { AudioClip } from './AudioClip';
import { Voice } from './Voice';

export type AudioSourceTarget = {
  /** Returns a vec3-like (vec3 from gl-matrix is a Float32Array of length 3). */
  position: vec3 | number[];
};

export type AudioSourceOptions = {
  bus: AudioBusId;
  /** Bus GainNode this source feeds into. */
  busInput: AudioNode;
  /** Initial linear volume in addition to clip.config.gain. */
  volume: number;
  /** True for loops; long-form beds/music. False fires a single decoded run. */
  loop: boolean;
  /** When provided alongside a non-null spatial clip config, voice is spatialized. */
  position: vec3 | null;
  /** Live-attached follower — overrides `position` each update tick. */
  attachTo: AudioSourceTarget | null;
  hrtfEnabled: boolean;
};

/**
 * Long-lived positional audio handle. Unlike one-shot voices (managed by VoicePool),
 * an AudioSource is owned by application code: it survives across pool eviction
 * pressure, and is destroyed explicitly by its owner (or via Scene cleanup).
 *
 * Tracks an attached RenderObject (if any) and pushes its position into the
 * underlying PannerNode on each update tick. Spatialization is automatic when
 * the clip has a spatial config and a position/attachTo is supplied.
 */
export class AudioSource {
  private voice: Voice | null = null;
  private _position: vec3;
  private _attachTo: AudioSourceTarget | null;
  private _volume: number;
  private _playing = false;
  private _destroyed = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly clip: AudioClip,
    private readonly opts: AudioSourceOptions,
  ) {
    this._position = vec3.fromValues(0, 0, 0);
    if (opts.position) vec3.copy(this._position, opts.position as vec3);
    this._attachTo = opts.attachTo;
    this._volume = opts.volume;
  }

  get playing(): boolean {
    return this._playing && !!this.voice && !this.voice.ended;
  }

  get destroyed(): boolean {
    return this._destroyed;
  }

  get clipId(): string {
    return this.clip.config.id;
  }

  play(): void {
    if (this._destroyed) return;
    if (this.voice && !this.voice.ended) return;
    const spatial = !!this.clip.config.spatial && this.hasSpatialPlacement();
    this.voice = new Voice(this.ctx, this.clip, {
      bus: this.opts.bus,
      busInput: this.opts.busInput,
      volume: this._volume,
      pitch: 1.0,
      position: spatial ? this._position : null,
      loop: this.opts.loop,
      hrtfEnabled: this.opts.hrtfEnabled,
    });
    this._playing = true;
    this.voice.onEnded(() => {
      // Loops don't fire ended; non-loop streaming/decoded do. Mark not-playing
      // but keep the source alive so play() can restart it.
      this._playing = false;
    });
  }

  stop(fadeSeconds: number = 0): void {
    if (!this.voice) return;
    this.voice.stop(fadeSeconds);
    this._playing = false;
  }

  setVolume(volume: number, fadeSeconds: number = 0): void {
    this._volume = volume;
    this.voice?.setVolume(this.clip.config.gain * volume, fadeSeconds);
  }

  setPosition(p: vec3): void {
    vec3.copy(this._position, p);
    this.voice?.setPosition(this._position);
  }

  attachTo(target: AudioSourceTarget | null): void {
    this._attachTo = target;
  }

  /** Called by AudioEngine.update each frame. Syncs attached-target position into the panner. */
  update(): void {
    if (!this._attachTo) return;
    const p = this._attachTo.position;
    this._position[0] = p[0];
    this._position[1] = p[1];
    this._position[2] = p[2];
    this.voice?.setPosition(this._position);
  }

  destroy(): void {
    if (this._destroyed) return;
    this._destroyed = true;
    this.voice?.stop();
    this.voice = null;
  }

  private hasSpatialPlacement(): boolean {
    return !!this._attachTo || this.opts.position != null;
  }
}
